import createClient from "openapi-fetch";
import { z } from "zod";
import type { JsonValue } from "@/api/json";
import type { paths } from "@/api/schema";

export const apiClient = createClient<paths>({
  baseUrl: "",
  credentials: "include",
});

interface OpenApiResult<T> {
  data?: T;
  error?: JsonValue;
  response: Response;
}

const errorPayloadSchema = z.object({
  code: z.string().optional(),
  message: z.union([z.string(), z.array(z.string())]).optional(),
}).passthrough();

export class ApiError extends Error {
  constructor(message: string, readonly code?: string) {
    super(message);
    this.name = "ApiError";
  }
}

async function errorPayload<T>(result: OpenApiResult<T>): Promise<JsonValue> {
  if (result.error !== undefined) return result.error;
  try {
    return await result.response.clone().json() as JsonValue;
  } catch {
    return null;
  }
}

export async function responseData<T>(request: Promise<OpenApiResult<T>>, authenticationPath = false): Promise<T> {
  const result = await request;
  // Algunos endpoints exitosos (por ejemplo, la última conciliación cuando aún no existe)
  // responden 200 sin cuerpo. El estado HTTP, no la presencia de data, define el éxito.
  if (result.response.ok) return result.data as T;
  if (result.response.status === 401 && !authenticationPath) window.location.assign("/acceso");
  const parsed = errorPayloadSchema.safeParse(await errorPayload(result));
  const message = parsed.success && parsed.data.message
    ? Array.isArray(parsed.data.message) ? parsed.data.message.join(" ") : parsed.data.message
    : "No se pudo completar la operación.";
  throw new ApiError(message, parsed.success ? parsed.data.code : undefined);
}
